import { useEffect, useState } from 'react';
import type { AiProviderKind } from '@cornerstone/shared';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import {
  BoPage,
  Section,
  ErrorBanner,
  OkBanner,
  inputCls,
  btnPrimary,
  btnGhost,
  errMsg,
} from './ui';

// =============================================================================
// BO · AI 초안 생성 모델 설정 (NBL_REQ_605 / HNBL-241)
//
// 운영사 전용. 공지 AI 초안 생성에 쓰는 provider/모델을 설정. provider 추상화
// 뒤에서 동작하므로 여기 설정만 바꾸면 모델이 교체된다. (기본: 설정 없음 → 서버
// env Vertex Gemini). API 키는 서버에 암호화 저장되며 다시 표시되지 않는다.
// =============================================================================

// LLM Gateway = provider 'openai'(OpenAI 호환) + 사내 게이트웨이 Base URL.
const GATEWAY_DEFAULT_BASE_URL = 'https://llm-gateway.ix-works.xyz/v1';

const PROVIDERS: ReadonlyArray<{ value: AiProviderKind; label: string; hint: string }> = [
  {
    value: 'openai',
    label: 'LLM Gateway (사내)',
    hint: 'OpenAI 호환 사내 게이트웨이. Base URL·API 키 입력 후 "모델 불러오기"로 선택.',
  },
  { value: 'ollama', label: 'Ollama (로컬/자체호스팅)', hint: 'Base URL + 모델 ID. API 키 불필요.' },
];

export function AiSettings(): JSX.Element {
  const [provider, setProvider] = useState<AiProviderKind>('openai');
  const [modelId, setModelId] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [timeoutMs, setTimeoutMs] = useState(15000);
  const [apiKey, setApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const cfg = await api.boGetAiConfig();
      if (cfg) {
        // Gemini는 목록에서 제거됨 → 레거시 설정은 LLM Gateway(openai)로 표시.
        setProvider(cfg.provider === 'gemini' ? 'openai' : cfg.provider);
        setModelId(cfg.modelId);
        setBaseUrl(cfg.baseUrl || (cfg.provider === 'openai' ? GATEWAY_DEFAULT_BASE_URL : ''));
        setTimeoutMs(cfg.timeoutMs);
        setHasApiKey(cfg.hasApiKey);
      } else {
        setBaseUrl(GATEWAY_DEFAULT_BASE_URL);
      }
      setLoaded(true);
      setErr(null);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function save() {
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const saved = await api.boSetAiConfig({
        provider,
        modelId: modelId.trim(),
        baseUrl: baseUrl.trim(),
        timeoutMs,
        // 빈 입력이면 키 미변경(undefined). 'clear'로 지우려면 별도 버튼.
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      setHasApiKey(saved.hasApiKey);
      setApiKey('');
      setOk('저장되었습니다.');
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function loadModels() {
    // 게이트웨이는 /v1/models에도 API 키를 요구한다. 입력 키도 없고 저장된 키도
    // 없으면 미리 막아 401→502 혼란을 방지.
    if (!apiKey.trim() && !hasApiKey) {
      setErr('게이트웨이 API 키를 먼저 입력한 뒤 "모델 불러오기"를 누르세요.');
      return;
    }
    setLoadingModels(true);
    setErr(null);
    try {
      const body: { baseUrl?: string; apiKey?: string } = {};
      if (baseUrl.trim()) body.baseUrl = baseUrl.trim();
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const { models: ids } = await api.boFetchAiModels(body);
      setModels(ids);
      if (ids.length === 0) setErr('모델 목록이 비어 있습니다.');
    } catch (e) {
      const m = errMsg(e);
      // 게이트웨이 오류는 원문(HTML/JSON) 대신 명확한 안내로.
      setErr(
        /401|missing api key|unauthor/i.test(m)
          ? '게이트웨이 API 키가 유효하지 않습니다. 키를 확인하고 다시 시도하세요.'
          : /502|bad gateway/i.test(m)
            ? '게이트웨이가 모델 목록을 반환하지 못했습니다(502). 게이트웨이 상태를 확인하세요. 모델 ID를 직접 입력해 저장할 수도 있습니다.'
            : `모델 목록을 불러오지 못했습니다: ${m.slice(0, 160)}`,
      );
    } finally {
      setLoadingModels(false);
    }
  }

  const meta = PROVIDERS.find((p) => p.value === provider) ?? PROVIDERS[0]!;

  return (
    <BoPage
      title="AI 모델 기본값"
      subtitle="운영사 전용 — 공지 파싱 + AI 초안 생성 모델의 기본값. 고객사 상세에서 전용 설정을 두지 않은 고객사와 채팅 연동에 적용됩니다. 변경 즉시 적용(재시작 불필요)."
      actions={
        <button type="button" className={btnGhost} onClick={() => void load()} disabled={loading}>
          새로고침
        </button>
      }
    >
      <ErrorBanner message={err} />
      <OkBanner message={ok} />
      <Section title="공지 파싱 · 초안 생성 모델">
        {loading && !loaded && !err ? <ContentSkeleton label="AI 모델 설정을 불러오는 중…" variant="form" /> : (
        <div className="flex flex-col gap-4 max-w-[560px]">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">Provider</span>
            <select
              className={inputCls}
              value={provider}
              onChange={(e) => {
                const v = e.target.value as AiProviderKind;
                setProvider(v);
                setModels([]);
                if (v === 'openai' && !baseUrl.trim()) setBaseUrl(GATEWAY_DEFAULT_BASE_URL);
              }}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="text-[12px] text-text-faint">{meta.hint}</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">Base URL</span>
            <input
              className={inputCls}
              value={baseUrl}
              placeholder={
                provider === 'ollama' ? 'http://127.0.0.1:11434' : GATEWAY_DEFAULT_BASE_URL
              }
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">모델 ID</span>
            {provider === 'openai' && models.length > 0 ? (
              <select
                className={inputCls}
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
              >
                <option value="">선택</option>
                {models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={inputCls}
                value={modelId}
                placeholder={provider === 'ollama' ? 'qwen3:4b' : 'openrouter/google/gemini-2.5-flash'}
                onChange={(e) => setModelId(e.target.value)}
              />
            )}
            {provider === 'openai' && (
              <button
                type="button"
                className={btnGhost}
                style={{ alignSelf: 'flex-start' }}
                onClick={() => void loadModels()}
                disabled={loadingModels}
              >
                {loadingModels
                  ? '불러오는 중…'
                  : models.length > 0
                    ? `모델 다시 불러오기 (${models.length})`
                    : '모델 불러오기'}
              </button>
            )}
          </label>

          {provider === 'openai' && (
            <label className="flex flex-col gap-1">
              <span className="text-[13px] text-text-dim">
                API Key {hasApiKey && <span className="text-green">· 저장됨</span>}
              </span>
              <input
                className={inputCls}
                type="password"
                value={apiKey}
                placeholder={hasApiKey ? '변경하려면 새 키 입력 (비우면 유지)' : 'sk-...'}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </label>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[13px] text-text-dim">타임아웃 (ms)</span>
            <input
              className={inputCls}
              type="number"
              min={1000}
              step={1000}
              value={timeoutMs}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n > 0) setTimeoutMs(Math.round(n));
              }}
            />
          </label>

          <div className="flex items-center gap-2 pt-1">
            <button type="button" className={btnPrimary} onClick={() => void save()} disabled={saving || loading}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
        )}
      </Section>
    </BoPage>
  );
}
